import "./ChatbotPublic.scss";
import {
  Avatar,
  Box,
  CircularProgress,
  Divider,
  Drawer,
  SvgIcon,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import IconButtonField from "../Button/IconButtonField";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import TextInputFieldC from "../TextInput/TextInputFieldC";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { ReactComponent as Send } from "../../Assets/Images/sendicon.svg";
import CloseIcon from "@mui/icons-material/Close";
import Markdown from "react-markdown";
import rehypeKatex from "rehype-katex";
import remarkMath from "remark-math";
import "katex/dist/katex.min.css";
import {
  chatbotPublic,
  clearChatbotPublicStatus,
} from "../../Redux/Slices/ChatbotPublic/ChatbotPublicSlicer";
import { ChatbotPublicProps, IChatbotPublic } from "./IChatbotPublic";
import AvatarImage from "../../Assets/Images/Chatbot_Avatar.png";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import { IQuestions } from "../../Pages/Instruction/IInstruction";
import { listListWritingExamPracticeSlice } from "../../Redux/Slices/Writing_Exam_Practice/ListWriting_Exam_PracticeSlice";
import CheckIcon from "@mui/icons-material/Check";
import {
  clearWritingChatbotPublicStatus,
  WritingchatbotPublic,
} from "../../Redux/Slices/ChatbotPublic/WritingChatbotPublicSlicer";
import {
  clearReadChatbotPublicStatus,
  ReadchatbotPublic,
} from "../../Redux/Slices/ChatbotPublic/ReadChatbotPublicSlicer";
import {
  clearExamWritingChatbotPublicStatus,
  ExamWritingchatbotPublic,
} from "../../Redux/Slices/ChatbotPublic/ExamWritingChatbotPublicSlicer";
import {
  clearExamReadChatbotPublicStatus,
  ExamReadchatbotPublic,
} from "../../Redux/Slices/ChatbotPublic/ExamReadChatbotPublicSlicer";
import { AskAIVideoLesson } from "../../Redux/Slices/UserVideoLessons/AskAIVideoLessons";
import { useLocation } from "react-router-dom";
import { Padding } from "@mui/icons-material";
import sendImage from "../../Assets/Images/Send_icon.svg";
import { motion, AnimatePresence } from "framer-motion";

const RotatingTextLoader = () => {
  const loadingTexts = [
    { text: "Thinking", emoji: "🤖..." },
    { text: "Analyzing", emoji: "🧠..." },
    { text: "Processing", emoji: "⚙️..." },
    { text: "Computing", emoji: "💻..." },
    { text: "Evaluating", emoji: "📊..." },
    { text: "Generating", emoji: "✨..." },
    { text: "Optimizing", emoji: "🚀..." },
    { text: "Learning", emoji: "📚..." },
    { text: "Reasoning", emoji: "🔎..." },
    { text: "Synthesizing", emoji: "🔗..." },
    { text: "Formulating", emoji: "💡..." },
    { text: "Calculating", emoji: "➗..." },
    { text: "Predicting", emoji: "📈..." },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    // Pick a random index initially
    setCurrentIndex(Math.floor(Math.random() * loadingTexts.length));

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % loadingTexts.length);
    }, 3000); // Change text every 3 seconds

    return () => clearInterval(interval);
  }, []);

  const currentItem = loadingTexts[currentIndex];

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
      <Box sx={{ display: "flex", gap: "4px" }}>
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: "#3b82f6",
              animation: "bounce 1.4s infinite ease-in-out both",
              animationDelay: `${i * 0.16}s`,
            }}
          />
        ))}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontStyle: "italic",
            fontWeight: 600,
            background:
              "linear-gradient(90deg, #4b5563 0%, #9ca3af 50%, #4b5563 100%)",
            backgroundSize: "200% 100%",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            animation: "shimmer 2s infinite linear",
            // key: currentItem.text, // Animation key handled by re-render or explicit key on Box if needed
          }}
        >
          {currentItem.text}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontStyle: "italic",
            fontWeight: 600,
            color: "#4b5563",
          }}
        >
          {currentItem.emoji}
        </Typography>
      </Box>
      <style>
        {`
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0); }
          40% { transform: scale(1); }
        }
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}
      </style>
    </Box>
  );
};

const TypewriterMarkdown = ({
  content,
  delay = 50,
  className,
  onUpdate,
  isLast,
}: {
  content: string;
  delay?: number;
  className?: string;
  onUpdate?: () => void;
  isLast?: boolean;
}) => {
  const [displayedContent, setDisplayedContent] = useState("");
  const [wordIndex, setWordIndex] = useState(0);

  const segments = useMemo(() => content.split(""), [content]);

  useEffect(() => {
    if (!isLast) {
      setDisplayedContent(content);
      setWordIndex(segments.length);
      return;
    }

    if (wordIndex < segments.length) {
      const timeout = setTimeout(() => {
        setDisplayedContent((prev) => prev + segments[wordIndex]);
        setWordIndex((prev) => prev + 1);
      }, 10); // Reduced delay for character-by-character smoothness
      return () => clearTimeout(timeout);
    }
  }, [segments, wordIndex, isLast, content]);

  useEffect(() => {
    onUpdate?.();
  }, [displayedContent, onUpdate]);

  return (
    <div
      className="typewriter-container"
      style={{
        position: "relative",
        display: "block",
        wordBreak: "break-word",
      }}
    >
      <Markdown
        remarkPlugins={[remarkMath]}
        rehypePlugins={[rehypeKatex]}
        className={className}
        components={{
          p: ({ children }) => <p>{children}</p>,
          h1: ({ children }) => <h1>{children}</h1>,
          h2: ({ children }) => <h2>{children}</h2>,
          h3: ({ children }) => <h3>{children}</h3>,
          ul: ({ children }) => <ul>{children}</ul>,
          ol: ({ children }) => <ol>{children}</ol>,
          li: ({ children }) => (
            <div style={{ display: "flex", alignItems: "flex-start", margin: "4px 0" }}>
              <span style={{ marginRight: "10px", flexShrink: 0, fontWeight: "bold", color: "#000000ff" }}>•</span>
              <div style={{ flex: 1 }}>{children}</div>
            </div>
          ),
          code: (props: any) => {
            const { children } = props;
            if (children && String(children).includes(":::cursor:::")) {
              return (
                <motion.span
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 0.6, repeat: Infinity }}
                  style={{
                    fontSize: "18px",
                    marginLeft: "2px",
                    display: "inline-block",
                    verticalAlign: "middle",
                    filter: "drop-shadow(0 0 2px rgba(59, 130, 246, 0.3))"
                  }}
                >
                  🖋️
                </motion.span>
              );
            }
            return <code>{children}</code>;
          },
        }}
      >
        {displayedContent.replace(/\n+$/, "") + (isLast && wordIndex < segments.length ? " `:::cursor:::`" : "")}
      </Markdown>
    </div>
  );
};

const ChatbotPublic: FunctionComponent<ChatbotPublicProps> = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const drawerWidth = isMobile ? "auto" : 450;

  const dispatch = useAppDispatch();
  const { data: chatbotData, isLoading: chatloading } = useAppSelector(
    (state) => state.ChatbotPublicSlicer
  );

  const { data: WritingChatbotData, isLoading: WritingChatloading } =
    useAppSelector((state) => state.WritingChatbotPublicSlicer);

  const { data: ReadChatbotData, isLoading: ReadChatloading } = useAppSelector(
    (state) => state.ReadChatbotPublicSlicer
  );

  const { data: ExamWritingChatbotData, isLoading: ExamWritingChatloading } =
    useAppSelector((state) => state.ExamWritingChatbotPublicSlicer);

  const { data: ExamReadChatbotData, isLoading: ExamReadChatloading } =
    useAppSelector((state) => state.ExamReadChatbotPublicSlicer);

  const { data: AskVideoLession, isLoading: VideoLession } = useAppSelector(
    (state) => state.AskAIVideoLessonsSliceReducer
  );





  const [feedback, setFeedback] = useState<string | null>(null);
  const { open, setOpen, state_value, review_screen, video_id, initialMessage } = props;
  const divRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const applicationName =
    window.location.hostname.toLowerCase().includes("ucat") ||
      window.location.hostname.toLowerCase().includes("ucattest")
      ? "ucat"
      : "gate";

  const inputRef = useRef<HTMLInputElement>(null);

  const viewQuestion = review_screen?.questions?.find((val: IQuestions) => ({
    ...val,
  }));

  // Clean ChatGPT HTML tags and normalize spacing
  const cleanMessage = (text = "") => {
    return text
      .replace(/<p><br><\/p>/g, "\n")
      .replace(/<br\s*\/?>/g, "\n")
      .replace(/<\/p>/g, "\n\n")
      .replace(/<p>/g, "")
      .replace(/<[^>]+>/g, "");
  };



  useEffect(() => {
    if (open) {
      dispatch(clearChatbotPublicStatus());
      dispatch(clearReadChatbotPublicStatus());
      dispatch(clearWritingChatbotPublicStatus());
      dispatch(clearExamReadChatbotPublicStatus());
      dispatch(clearExamWritingChatbotPublicStatus());
    }
  }, [open]);





  const review_screen_type = viewQuestion?.question_type_name;

  const practiceExam_type =
    review_screen?.exam_name || review_screen?.question_type;
  // console.log(viewQuestion, "type of ");

  const examviewQuestion = Array.isArray(review_screen?.questions)
    ? review_screen?.questions[0]
    : review_screen;

  const review_examscreen_type =
    examviewQuestion?.question_type_name ?? examviewQuestion?.question_type;
  const exam_highlighted_text = examviewQuestion?.response?.replace(
    /<[^>]+>/g,
    ""
  );
  const exam_explaination = examviewQuestion?.answer_explanation;

  const questions_id = viewQuestion?.question_id || examviewQuestion?.id;

  const highlighted_text = viewQuestion?.ai_response?.replace(/<[^>]+>/g, "");
  const methods = useForm<IChatbotPublic>();
  const formRef = useRef<HTMLFormElement>(null);

  const [chatHistory, setChatHistory] = useState<
    { sender: "user" | "bot"; message: string }[]
  >([]);

  //  console.log(practiceExam_type, "question in chatbot");
  // console.log(review_screen, "question in review");

  //  console.log(review_screen_type, "question in type");

  //     console.log(review_examscreen_type, "question in exam ");

  const dotloader =
    chatloading ||
    WritingChatloading ||
    ReadChatloading ||
    ExamWritingChatloading ||
    ExamReadChatloading ||
    VideoLession;

  const handleSendMessage = (message: string) => {
    if (dotloader) return;
    if (!message?.trim()) return;

    // Add user message to chat history
    setChatHistory((prev) => [...prev, { sender: "user", message: message }]);

    // Format chat history for payload
    const chatContext = chatHistory
      .map(
        (chat) =>
          `${chat.sender === "user" ? "user" : "bot"}: ${cleanMessage(
            chat.message
          )}`
      )
      .join(", ");

    const payloadMessage = `Previous message:[ ${chatContext} ] , user asked message: ${message}`;

    if (
      review_screen_type === "Writing" &&
      practiceExam_type === "Writing Test"
    ) {
      dispatch(
        WritingchatbotPublic({
          question_id: questions_id,
          student_test_id: state_value?.id,
          highlighted_text: highlighted_text,
          explanation: viewQuestion?.explanation,
          message: payloadMessage,
        })
      );
    } else if (review_examscreen_type === "Writing") {
      dispatch(
        ExamWritingchatbotPublic({
          question_id: questions_id,
          student_test_id: state_value,
          highlighted_text: exam_highlighted_text,
          explanation: exam_explaination,
          message: payloadMessage,
        })
      );
    } else if (
      review_screen_type === "Reading Comprehension" &&
      practiceExam_type === "Reading Comprehension"
    ) {
      dispatch(
        ReadchatbotPublic({
          question_id: questions_id,
          student_test_id: state_value,
          message: payloadMessage,
        })
      );
    } else if (review_examscreen_type === "Reading Comprehension") {
      dispatch(
        ExamReadchatbotPublic({
          question_id: questions_id,
          student_test_id: state_value,
          message: payloadMessage,
        })
      );
    } else if (location.pathname === "/videolessondetails") {
      dispatch(
        AskAIVideoLesson({
          message: payloadMessage,
          id: Number(video_id),
        })
      );
    } else {
      dispatch(
        chatbotPublic({
          message: payloadMessage,
          application: applicationName,
        })
      );
    }

    methods.reset({ message: "" });
  };

  const onSubmit: SubmitHandler<IChatbotPublic> = (data: any) => {
    handleSendMessage(data?.message);
  };

  useEffect(() => {
    if (open && initialMessage) {
      handleSendMessage(initialMessage);
    }
  }, [initialMessage]);




  useEffect(() => {
    if (
      chatbotData &&
      (typeof chatbotData === "string" || chatbotData?.message?.trim())
    ) {
      const botMessage =
        typeof chatbotData === "string"
          ? chatbotData
          : chatbotData.message || "";

      setChatHistory((prev) => [
        ...prev,
        { sender: "bot", message: botMessage },
      ]);
    }
  }, [chatbotData]);

  useEffect(() => {
    if (
      WritingChatbotData &&
      (typeof WritingChatbotData === "string" ||
        WritingChatbotData?.message?.trim())
    ) {
      const botMessage =
        typeof WritingChatbotData === "string"
          ? WritingChatbotData
          : WritingChatbotData.message || "";

      setChatHistory((prev) => [
        ...prev,
        { sender: "bot", message: botMessage },
      ]);
    }
  }, [WritingChatbotData]);

  useEffect(() => {


    if (
      AskVideoLession &&
      (typeof AskVideoLession === "string" || AskVideoLession?.data?.trim())
    ) {
      const botMessage =
        typeof AskVideoLession === "string"
          ? AskVideoLession
          : AskVideoLession.data || "";

      setChatHistory((prev) => [
        ...prev,
        { sender: "bot", message: botMessage },
      ]);
    }
  }, [AskVideoLession]);

  useEffect(() => {
    if (
      ReadChatbotData &&
      (typeof ReadChatbotData === "string" || ReadChatbotData?.message?.trim())
    ) {
      const botMessage =
        typeof ReadChatbotData === "string"
          ? ReadChatbotData
          : ReadChatbotData.message || "";

      setChatHistory((prev) => [
        ...prev,
        { sender: "bot", message: botMessage },
      ]);
    }
  }, [ReadChatbotData]);

  useEffect(() => {
    if (
      ExamWritingChatbotData &&
      (typeof ExamWritingChatbotData === "string" ||
        ExamWritingChatbotData?.message?.trim())
    ) {
      const botMessage =
        typeof ExamWritingChatbotData === "string"
          ? ExamWritingChatbotData
          : ExamWritingChatbotData.message || "";

      setChatHistory((prev) => [
        ...prev,
        { sender: "bot", message: botMessage },
      ]);
    }
  }, [ExamWritingChatbotData]);

  useEffect(() => {
    if (
      ExamReadChatbotData &&
      (typeof ExamReadChatbotData === "string" ||
        ExamReadChatbotData?.message?.trim())
    ) {
      const botMessage =
        typeof ExamReadChatbotData === "string"
          ? ExamReadChatbotData
          : ExamReadChatbotData.message || "";

      setChatHistory((prev) => [
        ...prev,
        { sender: "bot", message: botMessage },
      ]);
    }
  }, [ExamReadChatbotData]);

  useEffect(() => {
    if (divRef.current) {
      divRef.current.scrollTop = divRef.current.scrollHeight;
    }
  }, [chatHistory]);

  // useEffect(() => {
  //   if (location.pathname !== "/videolessondetails")
  //     dispatch(
  //       listListWritingExamPracticeSlice({
  //         student_test_id: Number(state_value?.id),
  //       })
  //     );
  // }, [state_value?.id]);

  useEffect(() => {
    // When loading finishes, focus input
    if (!dotloader) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [dotloader]);

  return (
    <>
      <Drawer
        sx={{
          width: drawerWidth,
          marginTop: "60px",
          "& .MuiDrawer-paper": {
            padding: 1,
            width: drawerWidth,
            display: "flex",
            flexDirection: "column",
            zIndex: 100000,
          },
        }}
        variant="persistent"
        anchor="right"
        open={open}
      >
        <header
          className="chat_bot_headerss"
          style={{
            display: "flex",
            alignItems: "center",
            padding: "12px 16px",
            gap: "12px",
          }}
        >
          <span
            style={{
              borderRadius: "50%",
              background: "linear-gradient(135deg, #2196f3 0%, #03a9f4 100%)",
              width: 40,
              height: 40,
              justifyContent: "center",
              padding: 4,
              fontSize: "14px",
            }}
          >
            <Avatar
              src={AvatarImage}
              className="chatbot-avatar"
              alt="Profile"
              sx={{ width: 32, height: 32 }}
            />
          </span>
          <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
            <Typography
              sx={{ fontWeight: 700, fontSize: "18px", lineHeight: 1.1 }}
            >
              Elliot
            </Typography>
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: "12px",
                color: "#6b7280",
                lineHeight: 1.1,
              }}
            >
              AI Study Assistant
            </Typography>
          </Box>
          <IconButtonField
            sx={{ ml: "auto" }}
            onClick={() => {
              setOpen(false);
              dispatch(clearChatbotPublicStatus());
              dispatch(clearReadChatbotPublicStatus());
              dispatch(clearWritingChatbotPublicStatus());
              dispatch(clearExamReadChatbotPublicStatus());
              dispatch(clearExamWritingChatbotPublicStatus());
            }}
          >
            <CloseIcon />
          </IconButtonField>
        </header>

        <Divider />

        <div
          ref={divRef}
          className="message_area"
          style={{ flexGrow: 1, overflowY: "auto" }}
        >
          <Box sx={{ p: 2 }}>
            {/* Intro message */}
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                mb: 1,
                display: "flex",
                gap: "10px",
                alignItems: "center",
              }}
            >
              <Avatar
                sx={{ bgcolor: "#009DFF", width: "20px", height: "20px" }}
                src={AvatarImage}
                className="chatbot-avatar"
                alt="Profile"
              />
              Elliot
            </Typography>
            <Box
              sx={{
                backgroundColor: "#f3f4f6",
                borderRadius: "12px",
                padding: "16px",
                maxWidth: "80%",
              }}
            >
              <Typography
                variant="body2"
                sx={{ lineHeight: 1.5, fontSize: "14px" }}
              >
                Hi! I'm Elliot, your AI study assistant. I'm here to help you
                with your studies and answer any questions you might have about
                your preparation!
              </Typography>
            </Box>
          </Box>

          <Box sx={{ px: 2, mb: 2 }}>
            {/* Feedback buttons
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
    <IconButtonField
      size="small"
      sx={{ display: "flex", alignItems: "center", gap: "6px" }}
    >
      <ThumbUpOffAltIcon fontSize="small" />
      <Typography variant="caption" sx={{ fontSize: "12px", fontWeight:"600" }}>
        Helpful
      </Typography>
    </IconButtonField>

    <IconButtonField
      size="small"
      sx={{ display: "flex", alignItems: "center", gap: "6px" }}
    >
      <ThumbDownOffAltIcon fontSize="small" />
      <Typography variant="caption" sx={{ fontSize: "12px" , fontWeight:"600"  }}>
        Not helpful
      </Typography>
    </IconButtonField>
  </Box> */}

            {/* Suggestion buttons */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {[
                "Study tips",
                "How can I improve?",
                "Practice recommendations",
                "General help",
              ].map((text, idx) => (
                <Box
                  key={idx}
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "5px",
                    px: 2,
                    py: 0.5,
                    fontSize: "12px",
                    fontWeight: "600",
                    cursor: dotloader ? "not-allowed" : "pointer",
                    backgroundColor: "#fff",
                    opacity: dotloader ? 0.6 : 1,
                    ":hover": {
                      backgroundColor: dotloader ? "#fff" : "#f1f5f9",
                    },
                  }}
                  onClick={() => {
                    handleSendMessage(text);
                  }}
                >
                  {text}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Mapped messages */}
          {chatHistory.map((entry, index) => (
            <Box
              key={index}
              sx={{
                mb: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: entry.sender === "user" ? "flex-end" : "flex-start",
              }}
            >
              {entry.sender === "bot" && (
                <>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 400,
                      mb: 1,
                      display: "flex",
                      gap: "10px",
                      alignItems: "center",
                      justifyContent: "flex-start",
                    }}
                  >
                    <Avatar
                      sx={{ bgcolor: "#009DFF", width: "20px", height: "20px" }}
                      src={AvatarImage}
                      alt="Profile"
                      className="blinking-avatar2"
                    />
                    Elliot
                  </Typography>
                </>
              )}
              <Box
                sx={{
                  backgroundColor: entry.sender === "user" ? "" : "#f3f4f6",
                  color: entry.sender === "user" ? "#fff" : "#333",
                  borderRadius: "16px",
                  padding: 1.5,
                  maxWidth: "85%",
                  fontSize: "15px",
                }}
              >
                {entry.sender === "bot" ? (
                  <TypewriterMarkdown
                    content={cleanMessage(entry.message)}
                    isLast={index === chatHistory.length - 1}
                    onUpdate={() => {
                      if (divRef.current) {
                        const { scrollTop, scrollHeight, clientHeight } = divRef.current;
                        // Only auto-scroll if the user is already near the bottom (within 150px)
                        const isAtBottom = scrollHeight - scrollTop - clientHeight < 150;
                        if (isAtBottom) {
                          divRef.current.scrollTop = scrollHeight;
                        }
                      }
                    }}
                    className="chat_bot_message"
                  />
                ) : (
                  <Markdown
                    children={cleanMessage(entry.message)}
                    remarkPlugins={[remarkMath]}
                    rehypePlugins={[rehypeKatex]}
                    className="user_message"
                  />
                )}
              </Box>
              {entry.sender === "bot" && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    mt: 1,
                    mb: 1,
                  }}
                >
                  <IconButtonField
                    size="small"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      backgroundColor:
                        feedback === `helpful-${index}` ? "#4caf50" : "#fff",
                      color: feedback === `helpful-${index}` ? "#fff" : "#333",
                      borderRadius: "8px",
                      fontWeight: 600,
                      px: 3,
                      py: 1,
                      boxShadow:
                        feedback === `helpful-${index}`
                          ? "0 2px 8px rgba(76,175,80,0.12)"
                          : "none",
                      transition: "background 0.2s, color 0.2s",
                      "&:hover": {
                        backgroundColor: "#4caf50",
                        color: "#fff",
                      },
                    }}
                    onClick={() => setFeedback(`helpful-${index}`)}
                  >
                    <ThumbUpOffAltIcon fontSize="small" />
                    Helpful
                    {feedback === `helpful-${index}` && (
                      <CheckIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </IconButtonField>
                  <IconButtonField
                    size="small"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      backgroundColor:
                        feedback === `not_helpful-${index}`
                          ? "#4caf50"
                          : "#fff",
                      color:
                        feedback === `not_helpful-${index}` ? "#fff" : "#333",
                      borderRadius: "8px",
                      fontWeight: 600,
                      px: 3,
                      py: 1,
                      boxShadow:
                        feedback === `not_helpful-${index}`
                          ? "0 2px 8px rgba(76,175,80,0.12)"
                          : "none",
                      transition: "background 0.2s, color 0.2s",
                      "&:hover": {
                        backgroundColor: "#4caf50",
                        color: "#fff",
                      },
                    }}
                    onClick={() => setFeedback(`not_helpful-${index}`)}
                  >
                    <ThumbDownOffAltIcon fontSize="small" />
                    Not helpful
                    {feedback === `not_helpful-${index}` && (
                      <CheckIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </IconButtonField>
                </Box>
              )}
            </Box>
          ))}
          {dotloader && (
            <Box
              sx={{
                mb: 2,
                px: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 400,
                  mb: 1,
                  display: "flex",
                  gap: "10px",
                  alignItems: "center",
                }}
              >
                <Avatar
                  sx={{ bgcolor: "#009DFF", width: "20px", height: "20px" }}
                  src={AvatarImage}
                  alt="Profile"
                  className="blinking-avatar2"
                />
                Elliot
              </Typography>
              <Box
                sx={{
                  backgroundColor: "#f3f4f6",
                  borderRadius: "16px",
                  padding: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1
                }}
              >
                <RotatingTextLoader />
              </Box>
            </Box>
          )}
        </div>

        <Divider />

        <footer className="chatbot_footer" style={{ padding: "0 16px 16px" }}>
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit(onSubmit)}
              ref={formRef}
              style={{
                display: "flex",
                width: "100%",
                maxWidth: "720px",
                marginTop: "25px",
                gap: "8px",
              }}
            >
              <div className="chat_main_block">
                <TextInputFieldC
                  name="message"
                  type="text"
                  className="chat_input"
                  size="small"
                  fullWidth
                  inputRef={inputRef}
                  disabled={dotloader} // <-- Add this line
                  placeholder={
                    dotloader
                      ? "Analyzing and responding... give us a moment"
                      : "Ask Elliot about any question or concept..."
                  }
                  InputProps={{
                    // startAdornment: chatloading && (
                    //   <ChatDotsLoading/>
                    // ),

                    sx: {
                      borderRadius: "24px",
                      border: "2px solid #f3f4f6",
                      paddingLeft: dotloader ? "40px" : "16px",
                    },
                  }}
                />
                <Typography
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    color: "#6b7280",
                    marginTop: "10px",
                  }}
                >
                  Elliot is here to help with your studies.
                </Typography>
              </div>
              <IconButtonField
                type="submit"
                disabled={dotloader}
                onClick={() => {
                  if (!dotloader) formRef.current?.requestSubmit();
                }}
                sx={{
                  background: "#009dff",
                  borderRadius: "12px",
                  padding: "0 12px",
                  height: "38px",
                  "&:hover": {
                    background: "#009dff",
                  },
                }}
              >
                <img
                  src={sendImage}
                  alt="Send"
                  style={{ width: 17, height: 17 }}
                />
                {/* <SvgIcon
                  component={Send}
                  sx={{
                    fontSize: "31px",
                    color:
                      chatloading ||
                      WritingChatloading ||
                      ReadChatloading ||
                      ExamWritingChatloading ||
                      ExamReadChatloading ||
                      VideoLession
                        ? "#bdbdbd"
                        : "#ffffffff",
                  }}
                  inheritViewBox
                /> */}
              </IconButtonField>
            </form>
          </FormProvider>
        </footer>
      </Drawer>
    </>
  );
};

export default ChatbotPublic;
